import { useCallback, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { startToolOperation, type ToolAnalyticsContext } from '@saas-pages/shared/public/runtime'
import {
  OpenImageViewerShell,
  OpenImageViewerError,
  type OpenImageViewerConfig,
  type OpenImageViewerLoadedImage,
} from '@/features/open-image-viewer'
import {
  MAX_TIFF_FILE_SIZE,
  TiffConversionError,
  convertTiffToJpg,
  cancelPendingTiffConversions,
  downloadBlob,
  outputFilename,
} from '@/features/tiff-converter'
import { useT } from '@/shared/i18n'

const ANALYTICS_KEY = 'tiff-viewer'
const JPG_QUALITY = 92
const TIFF_VIEWER_ANALYTICS = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/otkryt-tiff',
  utility: ANALYTICS_KEY,
  processingMode: 'local',
} satisfies ToolAnalyticsContext

interface TiffExportActionProps {
  file: File
  image: OpenImageViewerLoadedImage
}

function TiffExportAction({ file, image }: TiffExportActionProps) {
  const t = useT()
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleExport = useCallback(() => {
    if (!image.exportBlob || exporting) return

    setExporting(true)
    setError(null)
    const operation = startToolOperation(TIFF_VIEWER_ANALYTICS)
    const startTime = performance.now()

    try {
      downloadBlob(image.exportBlob, outputFilename(file.name))
      operation.success(performance.now() - startTime)
    } catch {
      setError(t.tiffViewer.exportError)
      operation.error('processing_failed')
    } finally {
      setExporting(false)
    }
  }, [exporting, file.name, image.exportBlob, t.tiffViewer.exportError])

  return (
    <div className="space-y-2">
      <Button
        type="button"
        className="w-full"
        disabled={!image.exportBlob || exporting}
        onClick={handleExport}
      >
        {exporting ? t.tiffViewer.exportingJpg : t.tiffViewer.downloadJpg}
      </Button>
      {error && <p className="text-sm leading-5 text-destructive">{error}</p>}
    </div>
  )
}

export function TiffViewerPage() {
  const t = useT()
  const config: OpenImageViewerConfig = {
    analyticsKey: ANALYTICS_KEY,
    analytics: TIFF_VIEWER_ANALYTICS,
    accept: '.tif,.tiff,image/tiff,image/x-tiff',
    acceptedMimeTypes: ['image/tiff', 'image/x-tiff'],
    acceptedExtensions: ['.tif', '.tiff'],
    maxFileSizeBytes: MAX_TIFF_FILE_SIZE,
    formatLabel: 'TIFF',
    loadImage: async (file) => {
      try {
        const result = await convertTiffToJpg(file, JPG_QUALITY, t.tiffToJpg.errorsText)
        const previewUrl = URL.createObjectURL(result.blob)
        return {
          previewUrl,
          width: result.width,
          height: result.height,
          mimeLabel: file.type || 'image/tiff',
          formatLabel: 'TIFF',
          facts: [{ label: t.tiffViewer.frames, value: String(result.frames) }],
          notice: result.frames > 1 ? t.tiffViewer.multiFrameWarning : undefined,
          exportBlob: result.blob,
          revoke: () => URL.revokeObjectURL(previewUrl),
        }
      } catch (error) {
        if (error instanceof TiffConversionError) {
          if (error.code === 'too_large_image') {
            throw new OpenImageViewerError(t.tiffViewer.errorsText.too_large_image, 'too_large_image')
          }
          if (error.code === 'invalid_signature' || error.code === 'corrupt_image') {
            throw new OpenImageViewerError(t.tiffViewer.errorsText.corrupt, 'corrupt')
          }
          throw new OpenImageViewerError(t.tiffViewer.errorsText.decode_failure, 'decode_failure')
        }
        throw error
      }
    },
    renderActions: ({ file, image }) => <TiffExportAction file={file} image={image} />,
    convertHref: '/tiff-v-jpg',
    relatedTools: t.tiffViewer.relatedTools,
    cancelPending: cancelPendingTiffConversions,
  }

  return <OpenImageViewerShell copy={t.tiffViewer} config={config} />
}
